/**
 * Threat Integration Layer
 * Rule: SDK -> Store bridge with UI feedback
 * This layer can use toast, store updates, and other UI concerns
 */
import { toast } from 'sonner';
import i18next from 'i18next';
import { useThreatStore } from '@/store/threat.store';
import { useTenantStore } from '@/store/tenant.store';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class ThreatIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * Threat list without updating global store (selector dialogs, asset detail card).
     */
    async fetchThreatsDetached(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: [], meta: { total: 0 }, uniqueValues: {} };
        }
        try {
            const page = params.page ?? 1;
            const pageSize = params.pageSize ?? params.limit ?? 10;
            const filterExtra = Object.entries(params).reduce((acc, [key, value]) => {
                if (key.startsWith('filter_') || key === 'excludeIds') acc[key] = value;
                return acc;
            }, {});
            const response = await this.service.getAll({
                page,
                pageSize,
                limit: pageSize,
                search: params.search ?? '',
                sortBy: params.sortBy ?? '',
                sortOrder: params.sortOrder ?? params.sortDirection ?? 'asc',
                tenantId,
                ...filterExtra,
            });
            const pagination = response.pagination || {};
            return {
                success: true,
                data: response.data || [],
                meta: {
                    total: pagination.total ?? 0,
                    page: pagination.page ?? page,
                    pages: pagination.totalPages ?? 0,
                },
                uniqueValues: response.uniqueValues || {},
            };
        } catch (error) {
            return {
                success: false,
                data: [],
                meta: { total: 0 },
                uniqueValues: {},
                error: this._getErrorMessage(error),
            };
        }
    }

    /**
     * Fetch threats and update store
     */
    async fetchThreats(page = 1, pageSize = 10, search = '', sortBy = '', sortOrder = 'asc', columnFilters = {}) {
        const store = useThreatStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);
        store.clearError();

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.getAll({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                tenantId,
                columnFilters
            });

            const { data, pagination, uniqueValues } = response;
            store.setThreats(data || []);

            // Map pagination from backend response payload
            if (pagination) {
                store.setPagination({
                    currentPage: pagination.page,
                    totalPages: pagination.totalPages,
                    totalItems: pagination.total,
                    itemsPerPage: pagination.pageSize,
                    hasNextPage: pagination.page < pagination.totalPages,
                    hasPreviousPage: pagination.page > 1,
                });
            }

            if (uniqueValues) {
                store.setUniqueValues(uniqueValues);
            }

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            store.setError(errorMessage);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Create threat and refresh list
     */
    async createThreat(data) {
        const store = useThreatStore.getState();
        const tenantId = useTenantStore.getState().selectedTenantId;

        store.setLoading(true);

        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.create({
                ...data,
                tenantId
            });

            toast.success(i18next.t('integrationToasts:threatCreatedSuccessfully', { defaultValue: 'Threat created successfully' }));
            store.cancelForm();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Update threat
     */
    async updateThreat(id, data) {
        const store = useThreatStore.getState();

        store.setLoading(true);

        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            const response = await this.service.update(id, data);

            toast.success(i18next.t('integrationToasts:threatUpdatedSuccessfully', { defaultValue: 'Threat updated successfully' }));
            store.cancelForm();

            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete threat
     */
    async deleteThreat(id) {
        const store = useThreatStore.getState();

        store.setLoading(true);

        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            await this.service.delete(id);

            toast.success(i18next.t('integrationToasts:threatDeletedSuccessfully', { defaultValue: 'Threat deleted successfully' }));

            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    /**
     * Delete multiple threats
     * @param {Array<string>} ids 
     */
    async bulkDeleteThreats(ids) {
        const store = useThreatStore.getState();
        store.setLoading(true);

        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }

        try {
            await this.service.bulkDelete(ids);
            toast.success(i18next.t('integrationToasts:nThreatsDeletedSuccessfully', {
                count: ids.length,
                defaultValue: `${ids.length} threats deleted successfully`,
            }));
            return { success: true };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error, { notFoundDefault: 'Threat not found' });
    }

    async saveControlAttribution(threatId, controlIds = []) {
        const store = useThreatStore.getState();
        store.setLoading(true);
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            store.setLoading(false);
            return { success: false, error: i18next.t('common:messages.tenantRequired', { defaultValue: 'Tenant selection is required' }) };
        }
        try {
            const response = await this.service.update(threatId, {
                countermeasures: controlIds,
            });
            toast.success(
                i18next.t('riskThreat:controlAttribution.saveSuccess', {
                    defaultValue: 'Control attribution saved',
                }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const errorMessage = this._getErrorMessage(error);
            toast.error(errorMessage);
            return { success: false, error: errorMessage };
        } finally {
            store.setLoading(false);
        }
    }
}
